import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { Button } from './ui/button';
import { PinActionIcon } from './icons/PinActionIcon';

export type PinConfirmMode = 'pin' | 'unpin';

export const DEFAULT_PIN_LIMIT = 3;

interface PinConfirmDrawerBaseProps {
  open: boolean;
  spaceName?: string;
  isLoading?: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

type PinConfirmDrawerProps =
  | (PinConfirmDrawerBaseProps & {
      mode: 'pin';
      /**
       * Current number of pinned posts in the space. When provided, renders a
       * `{current}/{limit} pins used` capacity hint so admins see available
       * headroom before committing to a pin.
       */
      currentPinCount?: number;
      /** Server-side cap for pins per space. Defaults to {@link DEFAULT_PIN_LIMIT}. */
      pinLimit?: number;
    })
  | (PinConfirmDrawerBaseProps & {
      mode: 'unpin';
      currentPinCount?: never;
      pinLimit?: never;
    });

interface DrawerCopy {
  title: string;
  description: (ctx: { spaceName?: string }) => React.ReactNode;
  confirmLabel: string;
  loadingLabel: string;
}

const PIN_COPY: Record<PinConfirmMode, DrawerCopy> = {
  pin: {
    title: 'Pin this post?',
    description: ({ spaceName }) =>
      spaceName ? (
        <>
          It moves to the top of <strong>{spaceName}</strong>. The author and members will be
          notified.
        </>
      ) : (
        <>It moves to the top of this space. The author and members will be notified.</>
      ),
    confirmLabel: 'Pin post',
    loadingLabel: 'Pinning...',
  },
  unpin: {
    title: 'Unpin this post?',
    description: ({ spaceName }) =>
      spaceName ? (
        <>
          It goes back to the <strong>{spaceName}</strong> feed. No notifications, no noise.
        </>
      ) : (
        <>It goes back to the space feed. No notifications, no noise.</>
      ),
    confirmLabel: 'Unpin post',
    loadingLabel: 'Unpinning...',
  },
};

export const PinConfirmDrawer: React.FC<PinConfirmDrawerProps> = ({
  open,
  mode,
  spaceName,
  isLoading = false,
  currentPinCount,
  pinLimit = DEFAULT_PIN_LIMIT,
  onConfirm,
  onOpenChange,
}) => {
  const copy = PIN_COPY[mode];
  const showCounter = mode === 'pin' && typeof currentPinCount === 'number';
  const atCapacity = showCounter && currentPinCount >= pinLimit;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="px-4 pb-safe">
        <DrawerHeader className="text-center pb-2">
          <div className="flex justify-center mb-2">
            <div className="w-12 h-12 rounded-full bg-mb-teal/15 flex items-center justify-center">
              <PinActionIcon className="w-6 h-6 text-mb-teal" />
            </div>
          </div>
          <DrawerTitle className="text-center">{copy.title}</DrawerTitle>
          <DrawerDescription className="text-center">
            {copy.description({ spaceName })}
          </DrawerDescription>
          {showCounter && (
            <p
              className={`text-center text-xs mt-2 ${
                atCapacity ? 'text-destructive' : 'text-muted-foreground'
              }`}
              aria-live="polite"
            >
              {currentPinCount}/{pinLimit} pins used
              {atCapacity ? '. Unpin one first.' : ''}
            </p>
          )}
        </DrawerHeader>
        <DrawerFooter className="pt-2">
          <Button
            onClick={onConfirm}
            disabled={isLoading || atCapacity}
            className="w-full bg-mb-teal text-black hover:bg-mb-teal/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {copy.loadingLabel}
              </>
            ) : (
              copy.confirmLabel
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isLoading}
            className="w-full shadow-none"
          >
            Cancel
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
